/**
 * Error Sanitizer
 *
 * Removes all personally identifiable information (PII) from errors
 * before they are sent to any monitoring service.
 */

import type { ErrorContext } from './error-handling';

export class ErrorSanitizer {
  // Patterns that might contain PII
  private readonly piiPatterns = [
    // Email addresses
    /[\w.-]+@[\w.-]+\.\w+/gi,
    // Phone numbers (various formats)
    /(\+?\d{1,3}[-.\s]?)?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}/g,
    // Social Security Numbers
    /\d{3}-\d{2}-\d{4}/g,
    // Credit card numbers
    /\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}/g,
    // IP addresses (IPv4)
    /\b(?:\d{1,3}\.){3}\d{1,3}\b/g,
    // IP addresses (IPv6)
    /(?:[a-f0-9]{1,4}:){7}[a-f0-9]{1,4}/gi,
    // UUIDs that might be user IDs
    /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi,
    // JWT tokens
    /eyJ[\w-]+\.[\w-]+\.[\w-]+/g,
    // API keys (common patterns)
    /[A-Za-z0-9]{32,}/g,
    // Passwords in URLs or strings
    /password[=:]["']?[\w\S]+/gi,
    /pwd[=:]["']?[\w\S]+/gi,
    /passwd[=:]["']?[\w\S]+/gi,
    // Tokens
    /token[=:]["']?[\w\S]+/gi,
    /api[_-]?key[=:]["']?[\w\S]+/gi,
    /secret[=:]["']?[\w\S]+/gi,
    // Names (basic heuristic - words after common prefixes)
    /name[=:]["']?[\w\s]+["']?/gi,
    /user[=:]["']?[\w\s]+["']?/gi,
  ];

  // Sensitive property names to redact from objects
  private readonly sensitiveKeys = new Set([
    'password',
    'pwd',
    'passwd',
    'secret',
    'token',
    'apikey',
    'api_key',
    'apiKey',
    'auth',
    'authorization',
    'cookie',
    'session',
    'sessionid',
    'session_id',
    'sessionId',
    'userid',
    'user_id',
    'userId',
    'username',
    'email',
    'phone',
    'ssn',
    'social_security',
    'creditcard',
    'credit_card',
    'creditCard',
    'cvv',
    'pin',
    'privatekey',
    'private_key',
    'privateKey',
    'birthdate',
    'dob',
    'address',
    'billing',
    'shipping',
  ]);

  /**
   * Sanitize an error object
   */
  sanitizeError(error: Error): Error {
    const sanitized = new Error(this.sanitizeString(error.message));
    sanitized.name = error.name;

    if (error.stack) {
      sanitized.stack = this.sanitizeString(error.stack);
    }

    // Copy over any custom properties, but sanitize them
    const customProps = Object.keys(error).filter(
      (key) => !['name', 'message', 'stack'].includes(key)
    );

    for (const prop of customProps) {
      const value = (error as any)[prop];
      (sanitized as any)[prop] = this.sanitizeValue(value);
    }

    return sanitized;
  }

  /**
   * Sanitize error context
   */
  sanitizeContext(context: ErrorContext): ErrorContext {
    const sanitized: ErrorContext = {
      severity: context.severity,
      category: context.category,
    };

    // Don't include user ID or session ID
    // Only include sanitized metadata
    if (context.metadata) {
      sanitized.metadata = this.sanitizeObject(context.metadata);
    }

    // Retry function is safe to copy
    if (context.retry) {
      sanitized.retry = context.retry;
    }

    return sanitized;
  }

  /**
   * Sanitize a string by removing PII
   */
  private sanitizeString(str: string): string {
    let sanitized = str;

    // Apply all PII patterns
    for (const pattern of this.piiPatterns) {
      sanitized = sanitized.replace(pattern, '[REDACTED]');
    }

    // Also redact file paths that might contain usernames
    sanitized = sanitized.replace(/\/Users\/[\w.-]+/g, '/Users/[REDACTED]');
    sanitized = sanitized.replace(/\/home\/[\w.-]+/g, '/home/[REDACTED]');
    sanitized = sanitized.replace(
      /C:\\Users\\[\w.-]+/g,
      'C:\\Users\\[REDACTED]'
    );

    return sanitized;
  }

  /**
   * Sanitize an object by removing sensitive keys and values
   */
  private sanitizeObject(
    obj: Record<string, unknown>
  ): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      // Check if the key itself is sensitive
      const lowerKey = key.toLowerCase();
      if (this.isSensitiveKey(lowerKey)) {
        sanitized[key] = '[REDACTED]';
        continue;
      }

      // Recursively sanitize the value
      sanitized[key] = this.sanitizeValue(value);
    }

    return sanitized;
  }

  /**
   * Sanitize any value type
   */
  private sanitizeValue(value: unknown): unknown {
    if (value === null || value === undefined) {
      return value;
    }

    if (typeof value === 'string') {
      return this.sanitizeString(value);
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.sanitizeValue(item));
    }

    if (typeof value === 'object') {
      return this.sanitizeObject(value as Record<string, unknown>);
    }

    // For functions and other types, just return a placeholder
    return '[COMPLEX_TYPE]';
  }

  /**
   * Check if a key name is sensitive
   */
  private isSensitiveKey(key: string): boolean {
    // Direct match
    if (this.sensitiveKeys.has(key)) {
      return true;
    }

    // Partial matches
    for (const sensitive of this.sensitiveKeys) {
      if (key.includes(sensitive)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Sanitize a URL by removing query parameters and fragments
   */
  sanitizeUrl(url: string): string {
    try {
      const parsed = new URL(url);
      // Only keep protocol, host, and pathname
      return `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
    } catch {
      // If URL parsing fails, return a generic placeholder
      return '[INVALID_URL]';
    }
  }

  /**
   * Generate a safe error fingerprint for deduplication
   */
  generateFingerprint(error: Error): string {
    const sanitizedMessage = this.sanitizeString(error.message);
    const errorType = error.name || 'Error';

    // Use only the first line of the stack trace (usually the error location)
    const firstStackLine = error.stack?.split('\n')[1]?.trim() || '';
    const sanitizedStack = this.sanitizeString(firstStackLine);

    return `${errorType}:${sanitizedMessage}:${sanitizedStack}`;
  }
}

// Export singleton instance
export const errorSanitizer = new ErrorSanitizer();
